import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { SeoProvider, Breadcrumbs } from "./Seo";

const NAV = [
  ["/sammenlign", "Sammenlign priser"],
  ["/behandlinger", "Behandlinger"],
  ["/dyreklinikker", "Dyreklinikker"],
  ["/for-klinikker", "For klinikker"],
  ["/om-vetpris", "Om VetPris"],
  ["/mit-dyr", "Mit dyr"],
];

const AccountActions = ({ onNav }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  if (!user)
    return (
      <>
        <Link to="/login" className="btn-outline btn-sm h-9" onClick={onNav} data-testid="header-login-link">Log ind</Link>
        <Link to="/login?tab=register" className="btn-primary btn-sm h-9" onClick={onNav} data-testid="header-register-link">Tilmeld klinik</Link>
      </>
    );
  const dash = user.platform_role === "SUPER_ADMIN" ? "/admin" : "/klinik/dashboard";
  return (
    <>
      <Link to={dash} className="btn-outline btn-sm h-9" onClick={onNav} data-testid="header-dashboard-link"><LayoutDashboard className="h-4 w-4" />Dashboard</Link>
      <button className="btn-sm h-9 inline-flex items-center gap-1 text-sm text-[#52706D] hover:text-[#153C3A]" data-testid="header-logout-button"
        onClick={async () => { await logout(); onNav?.(); nav("/"); }}><LogOut className="h-4 w-4" />Log ud</button>
    </>
  );
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const linkCls = ({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-[#087F72]" : "text-[#153C3A] hover:text-[#087F72]"}`;
  return (
    <header className="sticky top-0 z-50 bg-[#F7FAF9]/90 backdrop-blur-md border-b border-[#D2E5E1]">
      <div className="container-vp h-16 flex items-center justify-between gap-6">
        <Link to="/" data-testid="header-home-link" aria-label="VetPris forside"><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-7" aria-label="Hovedmenu">
          {NAV.map(([to, label]) => <NavLink key={to} to={to} className={linkCls} data-testid={`nav-${to.slice(1)}`}>{label}</NavLink>)}
        </nav>
        <div className="hidden lg:flex items-center gap-2"><AccountActions /></div>
        <button className="lg:hidden p-2 rounded-lg hover:bg-[#E6F4EF]" onClick={() => setOpen(!open)} aria-label="Åbn menu" aria-expanded={open} data-testid="mobile-menu-toggle">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#D2E5E1] bg-white" data-testid="mobile-menu">
          <nav className="container-vp py-4 flex flex-col gap-4">
            {NAV.map(([to, label]) => <NavLink key={to} to={to} className={linkCls} onClick={() => setOpen(false)} data-testid={`mobile-nav-${to.slice(1)}`}>{label}</NavLink>)}
            <div className="flex flex-wrap gap-2 pt-2"><AccountActions onNav={() => setOpen(false)} /></div>
          </nav>
        </div>
      )}
    </header>
  );
};

const FooterCol = ({ title, links }) => (
  <div>
    <h3 className="text-sm font-bold text-white mb-4">{title}</h3>
    <ul className="space-y-2.5">
      {links.map(([to, label]) => (
        <li key={to}><Link to={to} className="text-sm text-[#B9D6D0] hover:text-white transition-colors" data-testid={`footer-link-${to.replace(/\//g, "") || "home"}`}>{label}</Link></li>
      ))}
    </ul>
  </div>
);

export const OwnerNote = ({ light = false, testid = "owner-note" }) => (
  <p className={`text-xs flex flex-wrap items-center gap-2 ${light ? "text-[#B9D6D0]" : "text-[#52706D]"}`} data-testid={testid}>
    <img src="/images/vetcura-logo.png" alt="VetCura" className="h-7 w-7 rounded-md bg-white object-contain" width="28" height="28" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
    <span>VetPris er ejet og drevet af <a href="https://vetcura.info" target="_blank" rel="noopener" className={`font-semibold underline ${light ? "text-white" : "text-[#087F72]"}`} data-testid={`${testid}-link`}>VetCura</a> (vetcura.info).</span>
  </p>
);

const Footer = () => (
  <footer className="bg-[#153C3A] text-[#B9D6D0] mt-24">
    <div className="container-vp py-14 grid gap-10 md:grid-cols-6">
      <div className="md:col-span-2">
        <Logo light />
        <p className="text-sm mt-4 leading-relaxed">Sammenlign dyrlægepriser. Vælg med overblik.</p>
        <div className="mt-4"><OwnerNote light testid="footer-owner" /></div>
        <p className="text-xs mt-4 text-[#8FB5AE]">VetPris er en uafhængig prisportal. Klinikdata fra OpenStreetMap (© OpenStreetMap-bidragydere, ODbL). Postnumre: GeoNames (CC BY 4.0).</p>
      </div>
      <FooterCol title="Find priser" links={[["/sammenlign", "Sammenlign priser"], ["/behandlinger", "Behandlinger"], ["/dyreklinikker", "Dyreklinikker"], ["/guide", "Guide til priser"]]} />
      <FooterCol title="Dyrlæge i din by" links={[["/dyrlaege/koebenhavn", "Dyrlæge København"], ["/dyrlaege/aarhus", "Dyrlæge Aarhus"], ["/dyrlaege/odense", "Dyrlæge Odense"], ["/dyrlaege/aalborg", "Dyrlæge Aalborg"], ["/dyrlaege", "Alle byer"], ["/akut-dyrlaege", "Akut dyrlæge"]]} />
      <FooterCol title="VetPris" links={[["/om-vetpris", "Om VetPris"], ["/mit-dyr", "VetCura Pet – gratis dyrejournal"], ["/for-klinikker", "For klinikker"], ["/kontakt", "Kontakt"], ["/login", "Log ind"]]} />
      <FooterCol title="Juridisk" links={[["/privatlivspolitik", "Privatlivspolitik"], ["/vilkaar", "Vilkår for brug"], ["/cookies", "Cookiepolitik"]]} />
    </div>
    <div className="border-t border-white/10">
      <div className="container-vp py-5 text-xs text-[#8FB5AE] flex flex-col sm:flex-row justify-between gap-2">
        <span>© {new Date().getFullYear()} VetPris. Alle rettigheder forbeholdes.</span>
        <span>Priser er vejledende. Kontakt altid klinikken for endelig pris.</span>
      </div>
    </div>
  </footer>
);

export const PublicLayout = () => (
  <SeoProvider>
    <div className="min-h-screen flex flex-col bg-[#F7FAF9]">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-white p-2 rounded z-[60]">Spring til indhold</a>
      <Header />
      <Breadcrumbs />
      <main id="main" className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  </SeoProvider>
);

export const PageHeader = ({ eyebrow, title, children }) => (
  <section className="bg-[#E6F4EF] border-b border-[#D2E5E1]">
    <div className="container-vp py-12 sm:py-16">
      {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#153C3A] max-w-3xl">{title}</h1>
      {children && <div className="mt-4 text-base md:text-lg text-[#52706D] max-w-2xl">{children}</div>}
    </div>
  </section>
);

export const Loading = () => <div className="py-16 min-h-[100vh] text-center text-sm text-[#52706D]" data-testid="loading">Indlæser…</div>;
